import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Badge,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  Nav,
  NavItem,
  NavLink
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerSettlements } from "../../store/customerSettlements/actions";
import { fetchProviderErpInvoices } from "../../store/providerErpInvoices/actions";
import TableContainer from "../../components/Common/TableContainer";
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import { FiExternalLink, FiDownload, FiFileText } from "react-icons/fi";
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const exportElementToPdf = async (element, fileName) => {
  if (!element) return;

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const contentHeight = pageHeight - margin * 2;

  const sections = Array.from(element.querySelectorAll(".pdf-section"));
  const targets = sections.length ? sections : [element];
  let cursorY = margin;

  const addCanvasWithPagination = (canvas) => {
    const pxPerMm = canvas.width / contentWidth;
    let sourceY = 0;
    let remainingHeightPx = canvas.height;

    while (remainingHeightPx > 0) {
      const availableMm = pageHeight - margin - cursorY;

      if (availableMm <= 0.1) {
        pdf.addPage();
        cursorY = margin;
        continue;
      }

      const maxSliceHeightPx = Math.max(1, Math.floor(availableMm * pxPerMm));
      const sliceHeightPx = Math.min(remainingHeightPx, maxSliceHeightPx);

      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sliceHeightPx;

      const sliceContext = sliceCanvas.getContext("2d");
      if (!sliceContext) return;

      sliceContext.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        canvas.width,
        sliceHeightPx
      );

      const sliceHeightMm = sliceHeightPx / pxPerMm;
      pdf.addImage(
        sliceCanvas.toDataURL("image/png"),
        "PNG",
        margin,
        cursorY,
        contentWidth,
        sliceHeightMm,
        undefined,
        "FAST"
      );

      cursorY += sliceHeightMm;
      sourceY += sliceHeightPx;
      remainingHeightPx -= sliceHeightPx;

      if (remainingHeightPx > 0) {
        pdf.addPage();
        cursorY = margin;
      }
    }
  };

  for (let index = 0; index < targets.length; index += 1) {
    const canvas = await html2canvas(targets[index], {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    const sectionHeightMm = (canvas.height * contentWidth) / canvas.width;
    const shouldStartNewPage =
      sectionHeightMm <= contentHeight && cursorY + sectionHeightMm > pageHeight - margin;

    if (shouldStartNewPage) {
      pdf.addPage();
      cursorY = margin;
    }

    addCanvasWithPagination(canvas);

    if (index < targets.length - 1) {
      cursorY += 2;
      if (cursorY > pageHeight - margin) {
        pdf.addPage();
        cursorY = margin;
      }
    }
  }

  pdf.save(fileName);
};

const SettlementStatements = () => {
  document.title = "بيانات التسويات | DBsaloni Service Provider";
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("1");

  // Fetch settlements on component mount
  useEffect(() => {
    dispatch(fetchCustomerSettlements());
    dispatch(fetchProviderErpInvoices());
  }, [dispatch]);

  return (
    <div className="page-content" dir="rtl">
      <Container fluid>
        <Breadcrumbs title="بيانات التسويات" breadcrumbItem="التسويات" />

        {/* Navigation Tabs */}
        <Card className="mb-4">
          <CardBody className="pb-0">
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => setActiveTab("1")}
                  style={{ cursor: "pointer" }}
                >
                  <FiFileText className="me-1" />
                  بيانات التسويات
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => setActiveTab("2")}
                  style={{ cursor: "pointer" }}
                >
                  <FiExternalLink className="me-1" />
                  فواتير العمولة
                </NavLink>
              </NavItem>
            </Nav>
          </CardBody>
        </Card>

        {/* Tab 1: Settlement Statements */}
        {activeTab === "1" && (
          <SettlementTab />
        )}

        {/* Tab 2: ERP Invoices */}
        {activeTab === "2" && (
          <ErpInvoicesTab />
        )}
      </Container>
    </div>
  );
};

// Settlement Statements Tab Component
const SettlementTab = () => {
  const dispatch = useDispatch();
  const { settlements, loading, error } = useSelector((state) => state.customerSettlements);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);


  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const viewDetails = (settlement) => {
    setSelectedSettlement(settlement);
    toggleModal();
  };

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'تاريخ غير صالح';
    }
  };

  const formatCurrency = (value) => {
    return `${(parseFloat(value) || 0).toFixed(2)} ر.س`;
  };

  const isRefundSettlement = (settlement) =>
    settlement?.is_refund_settlement === true || settlement?.settlement_type === "refund";

  const groupedSettlements = useMemo(() => {
    const list = Array.isArray(settlements) ? settlements : [];
    const groups = new Map();

    const toNumber = (value) => Number(value || 0);
    const toDate = (value) => {
      try {
        if (!value) return new Date(0);
        return typeof value === 'string' ? new Date(value) : new Date(value);
      } catch {
        return new Date(0);
      }
    };

    list.forEach((settlement) => {
      const batchId = settlement.batch_settlement_id;

      if (batchId) {
        const key = `batch-${batchId}`;
        const current = groups.get(key);
        const amounts = settlement.amounts || {};
        const createdAt = toDate(settlement.created_at);

        if (!current) {
          groups.set(key, {
            ...settlement,
            is_batched: true,
            invoice_count: settlement.invoice_count || 1,
            is_refund_settlement: isRefundSettlement(settlement),
            settlement_type: isRefundSettlement(settlement) ? "refund" : "standard",
            amounts: {
              original_service_amount: toNumber(amounts.original_service_amount),
              platform_commission: toNumber(amounts.platform_commission),
              app_commission: toNumber(amounts.app_commission),
              service_provider_commission: toNumber(amounts.service_provider_commission),
              tamara_commission: toNumber(amounts.tamara_commission),
              app_commission_vat: toNumber(amounts.app_commission_vat),
              service_provider_commission_vat: toNumber(amounts.service_provider_commission_vat),
              tamara_commission_vat: toNumber(amounts.tamara_commission_vat),
              amount_after_commission: toNumber(amounts.amount_after_commission),
              service_vat: toNumber(amounts.service_vat),
              net_amount_with_vat: toNumber(amounts.net_amount_with_vat),
              cancellation_fines: toNumber(amounts.cancellation_fines),
              final_payout: toNumber(amounts.final_payout),
              gross_service_amount: toNumber(amounts.gross_service_amount),
              service_net: toNumber(amounts.service_net),
              net_after_fines: toNumber(amounts.net_after_fines ?? amounts.service_net),
            },
            batch_invoice_ids: settlement.invoice_id ? [settlement.invoice_id] : [],
            _date: createdAt,
          });
        } else {
          current.amounts.original_service_amount += toNumber(amounts.original_service_amount);
          current.amounts.platform_commission += toNumber(amounts.platform_commission);
          current.amounts.app_commission += toNumber(amounts.app_commission);
          current.amounts.service_provider_commission += toNumber(amounts.service_provider_commission);
          current.amounts.tamara_commission += toNumber(amounts.tamara_commission);
          current.amounts.app_commission_vat += toNumber(amounts.app_commission_vat);
          current.amounts.service_provider_commission_vat += toNumber(amounts.service_provider_commission_vat);
          current.amounts.tamara_commission_vat += toNumber(amounts.tamara_commission_vat);
          current.amounts.amount_after_commission += toNumber(amounts.amount_after_commission);
          current.amounts.service_vat += toNumber(amounts.service_vat);
          current.amounts.net_amount_with_vat += toNumber(amounts.net_amount_with_vat);
          current.amounts.cancellation_fines += toNumber(amounts.cancellation_fines);
          current.amounts.final_payout += toNumber(amounts.final_payout);
          current.amounts.gross_service_amount += toNumber(amounts.gross_service_amount);
          current.amounts.service_net += toNumber(amounts.service_net);
          current.amounts.net_after_fines += toNumber(amounts.net_after_fines ?? amounts.service_net);
          current.is_refund_settlement = current.is_refund_settlement || isRefundSettlement(settlement);
          current.settlement_type = current.is_refund_settlement ? "refund" : "standard";
          current.invoice_count = (current.invoice_count || 0) + (settlement.invoice_count || 1);
          if (settlement.invoice_id) {
            current.batch_invoice_ids = Array.from(new Set([...(current.batch_invoice_ids || []), settlement.invoice_id]));
          }
          if (createdAt > (current._date || new Date(0))) {
            current._date = createdAt;
            current.created_at = settlement.created_at;
          }
          groups.set(key, current);
        }
      } else {
        groups.set(`single-${settlement.id}`, {
          ...settlement,
          is_batched: false,
          is_refund_settlement: isRefundSettlement(settlement),
          settlement_type: isRefundSettlement(settlement) ? "refund" : "standard",
          invoice_count: 1,
        });
      }
    });

    return Array.from(groups.values()).map(({ _date, ...rest }) => rest);
  }, [settlements]);

  const statistics = useMemo(() => {
    const list = Array.isArray(settlements) ? settlements : [];
    
    const stats = {
      totalSettlements: list.length,
      singleSettlements: 0,
      batchSettlements: 0,
      refundSettlements: 0,
      totalOriginalAmount: 0,
      totalCommissions: 0,
      totalTamaraCommissions: 0,
      totalFines: 0,
      totalVAT: 0,
      totalPayouts: 0,
    };

    list.forEach((settlement) => {
      if (settlement.batch_settlement_id) {
        stats.batchSettlements += 1;
      } else {
        stats.singleSettlements += 1;
      }

      if (isRefundSettlement(settlement)) {
        stats.refundSettlements += 1;
        return;
      }

      const amounts = settlement.amounts || {};
      stats.totalOriginalAmount += Number(amounts.original_service_amount || 0);
      stats.totalCommissions += Number(amounts.platform_commission || 0);
      stats.totalTamaraCommissions += Number(amounts.tamara_commission || 0);
      stats.totalFines += Number(amounts.cancellation_fines || 0);
      stats.totalVAT += Number(amounts.service_vat || 0);
      stats.totalPayouts += Number(amounts.final_payout || 0);
    });

    return stats;
  }, [settlements]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        width: 50
      },
      {
        Header: "نوع التسوية",
        accessor: "is_batched",
        Cell: ({ value }) => (
          <Badge color={value ? "info" : "primary"}>
            {value ? "مجمعة" : "فردية"}
          </Badge>
        ),
        width: 80
      },
      {
        Header: "نوع العملية",
        accessor: "settlement_type",
        Cell: ({ value, row }) => {
          const isRefund = value === "refund" || row.original.is_refund_settlement;
          return (
            <Badge color={isRefund ? "danger" : "success"}>
              {isRefund ? "استرجاع" : "عادية"}
            </Badge>
          );
        },
        width: 90,
      },
      {
        Header: "المبلغ النهائي",
        accessor: "amounts.final_payout",
        Cell: ({ value, row }) => {
          const finalAmount = value !== undefined ? value : (
            (row.original.amounts?.net_amount_with_vat || row.original.amounts?.net_after_fines || 0) - 
            (row.original.amounts?.cancellation_fines || 0)
          );
          const isRefund = row.original.settlement_type === "refund" || row.original.is_refund_settlement;
          return (
            <strong className={isRefund ? "text-danger" : "text-success"}>
              {formatCurrency(finalAmount)}
            </strong>
          );
        },
      },
      {
        Header: "عمولة تمارا",
        accessor: "amounts.tamara_commission",
        Cell: ({ value }) => {
          const amount = Number(value || 0);
          return amount > 0 ? <span className="text-danger">{formatCurrency(amount)}</span> : "0.00 ر.س";
        },
      },
      {
        Header: "التاريخ",
        accessor: "created_at",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "الحالة",
        accessor: "status",
        Cell: ({ value, row }) => {
          const isRefund = row.original.settlement_type === "refund" || row.original.is_refund_settlement;
          return (
            <Badge color={isRefund ? "danger" : "success"} className="p-2">
              {isRefund ? "استرجاع" : value === "generated" ? "تم الإنشاء" : value}
            </Badge>
          );
        },
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            onClick={() => viewDetails(row.original)}
            className="d-flex align-items-center"
          >
            <FiExternalLink className="me-1" />
            عرض
          </Button>
        ),
        width: 100
      },
    ],
    []
  );

  return (
    <>
      {/* Statistics Cards */}
      {!loading && !error && groupedSettlements.length > 0 && (
        <Row className="mb-4">
          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-success shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">إجمالي المبالغ الأصلية</h6>
                    <h4 className="text-success">{(statistics.totalOriginalAmount).toFixed(2)} ر.س</h4>
                    <small className="text-muted">(بدون تسويات الاسترجاع)</small>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#28a745", opacity: 0.3 }}>
                    💰
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-warning shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">إجمالي العمولات</h6>
                    <h4 className="text-warning">{(statistics.totalCommissions).toFixed(2)} ر.س</h4>
                    <small className="text-muted">(بدون تسويات الاسترجاع)</small>
                    <small className="text-danger d-block">تمارا: {(statistics.totalTamaraCommissions).toFixed(2)} ر.س</small>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#ffc107", opacity: 0.3 }}>
                    ⚠️
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-danger shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">إجمالي الغرامات</h6>
                    <h4 className="text-danger">{(statistics.totalFines).toFixed(2)} ر.س</h4>
                    <small className="text-muted">(بدون تسويات الاسترجاع)</small>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#dc3545", opacity: 0.3 }}>
                    📛
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-info shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">إجمالي الضريبة</h6>
                    <h4 className="text-info">{(statistics.totalVAT).toFixed(2)} ر.س</h4>
                    <small className="text-muted">(بدون تسويات الاسترجاع)</small>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#17a2b8", opacity: 0.3 }}>
                    📊
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Total Payout Summary Card */}
      {!loading && !error && groupedSettlements.length > 0 && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="bg-gradient shadow-lg">
              <CardBody>
                <Row>
                  <Col md={4}>
                    <div className="text-center border-end">
                      <h6 className="text-muted mb-2">التسويات الفردية</h6>
                      <h3 className="text-info">{statistics.singleSettlements}</h3>
                      <small className="text-muted">Single Settlements</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center border-end">
                      <h6 className="text-muted mb-2">التسويات المجمعة</h6>
                      <h3 className="text-warning">{statistics.batchSettlements}</h3>
                      <small className="text-muted">Batch Settlements</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <h6 className="text-muted mb-2">إجمالي المبالغ المستحقة</h6>
                      <h3 style={{ color: '#198754', fontWeight: 'bold' }}>{(statistics.totalPayouts).toFixed(2)} ر.س</h3>
                      <small className="text-muted">بدون تسويات الاسترجاع</small>
                    </div>
                  </Col>
                </Row>
                <div className="text-center mt-3">
                  <Badge color="danger" className="px-3 py-2">
                    تسويات الاسترجاع: {statistics.refundSettlements}
                  </Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">جاري تحميل البيانات...</p>
        </div>
      ) : error ? (
        <Alert color="danger">
          <h5 className="alert-heading">حدث خطأ!</h5>
          <p>{error}</p>
          <Button
            color="danger"
            size="sm"
            onClick={() => dispatch(fetchCustomerSettlements())}
          >
            إعادة المحاولة
          </Button>
        </Alert>
      ) : groupedSettlements.length === 0 ? (
        <Alert color="info" className="text-center">
          <FiFileText className="me-2" />
          لا توجد بيانات تسويات متاحة حالياً
        </Alert>
      ) : (
        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={groupedSettlements}
              isGlobalFilter={true}
              isAddOptions={false}
              customPageSize={10}
              className="custom-header-css"
            />
          </CardBody>
        </Card>
      )}

      <SettlementDetailsModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        settlement={selectedSettlement}
      />
    </>
  );
};

const SettlementDetailsModal = ({ isOpen, toggle, settlement }) => {
  const settlementRef = useRef();
  const [loadingPDF, setLoadingPDF] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => settlementRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          direction: rtl;
        }
        .settlement-header {
          border-bottom: 2px solid #000;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .settlement-details {
          background-color: #f9f9f9;
          padding: 15px;
          border-radius: 5px;
        }
        .settlement-container .pdf-section,
        .settlement-container .settlement-details,
        .settlement-container .invoice-header,
        .settlement-container .settlement-footer,
        .settlement-container table,
        .settlement-container tr,
        .settlement-container .row {
          break-inside: avoid;
          page-break-inside: avoid;
        }
      }
    `,
  });

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('Date parsing error:', error);
      return 'تاريخ غير صالح';
    }
  };

  const formatCurrency = (value) => {
    return `${(parseFloat(value) || 0).toFixed(2)} ر.س`;
  };

  const isRefundSettlement =
    settlement?.is_refund_settlement === true || settlement?.settlement_type === "refund";

  const downloadPDF = async () => {
    if (!settlement || !settlementRef.current || loadingPDF) return;

    try {
      setLoadingPDF(true);
      await exportElementToPdf(
        settlementRef.current,
        `settlement_${settlement.id?.slice(0, 8) || 'N/A'}.pdf`
      );
    } catch (error) {
      console.error("Error downloading settlement PDF:", error);
    } finally {
      setLoadingPDF(false);
    }
  };

  if (!settlement) return null;

  const getDisplayValue = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      if (typeof value === "string" && value.trim() === "") continue;
      return value;
    }
    return "غير متوفر";
  };

  const platformInfo = settlement?.platform || {};
  const providerInfo =
    settlement?.service_provider ||
    settlement?.provider ||
    settlement?.current_provider ||
    {};

  const platformName = getDisplayValue(
    platformInfo.company_name,
    platformInfo.erp_admin_customer_name,
    "DB Saloni"
  );

  const providerName = getDisplayValue(
    providerInfo.name,
    providerInfo.company_name,
    settlement?.service_provider_name,
    settlement?.provider_name,
    settlement?.service_provider_id
  );

  const providerCommercialRegister = getDisplayValue(
    providerInfo.commercial_register,
    providerInfo.commercialRegister,
    providerInfo.cr_number,
    providerInfo.crNumber,
    settlement?.service_provider_commercial_register,
    settlement?.provider_commercial_register,
    settlement?.commercial_register
  );

  const providerVatNumber = getDisplayValue(
    providerInfo.vat_number,
    providerInfo.vatNumber,
    providerInfo.tax_number,
    providerInfo.taxNumber,
    providerInfo.tax_id,
    providerInfo.taxId,
    settlement?.service_provider_vat_number,
    settlement?.service_provider_tax_number,
    settlement?.provider_vat_number,
    settlement?.provider_tax_number,
    settlement?.tax_number,
    settlement?.vat_number
  );

  const providerUnifiedNo = getDisplayValue(
    providerInfo.entity_unified_no,
    providerInfo.entityUnifiedNo,
    providerInfo.unified_number,
    providerInfo.unifiedNumber,
    settlement?.service_provider_entity_unified_no,
    settlement?.provider_entity_unified_no,
    settlement?.entity_unified_no
  );

  const providerAddress = getDisplayValue(
    providerInfo.address,
    providerInfo.location,
    settlement?.service_provider_address,
    settlement?.provider_address,
    settlement?.address
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-0">
        <span className="modal-title">بيان التسوية - Settlement Statement</span>
      </ModalHeader>
      <ModalBody>
        <div className="settlement-container" ref={settlementRef}>
          <div className="settlement-header text-center mb-4 pdf-section">
            <h2 className="settlement-title">بيان التسوية</h2>
            <p className="text-muted">Settlement Statement</p>
          </div>

          <img
            src="/logo.png"
            alt="Company Logo"
            style={{ height: 60, objectFit: "contain" }}
            className="mb-4 d-block mx-auto"
          />

          {/* Invoice From/To Header */}
          <div className="invoice-header mb-4 p-3 border rounded bg-light pdf-section">
            <h6 className="mb-3 text-center">فاتورة التسوية</h6>
            <Row className="g-3 align-items-stretch">
              <Col md={6}>
                <div className="h-100 p-3 bg-white border rounded text-end">
                  <h6 className="mb-2">من: بيانات المنصة (DB Saloni)</h6>
                  <p className="mb-1"><strong>الاسم:</strong> {platformName}</p>
                  <p className="mb-1"><strong>السجل التجاري:</strong> {getDisplayValue(platformInfo.commercial_register, platformInfo.commercialRegister)}</p>
                  <p className="mb-1"><strong>الرقم الضريبي:</strong> {getDisplayValue(platformInfo.vat_number, platformInfo.vatNumber, platformInfo.tax_number, platformInfo.taxNumber)}</p>
                  {/* <p className="mb-1"><strong>الرقم الموحد:</strong> {getDisplayValue(platformInfo.entity_unified_no, platformInfo.entityUnifiedNo)}</p> */}
                  <p className="mb-0"><strong>العنوان:</strong> {getDisplayValue(platformInfo.address)}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="h-100 p-3 bg-white border rounded">
                  <h6 className="mb-2">إلى: بيانات مقدم الخدمة</h6>
                  <p className="mb-1"><strong>الاسم:</strong> {providerName}</p>
                  <p className="mb-1"><strong>السجل التجاري:</strong> {providerCommercialRegister}</p>
                  <p className="mb-1"><strong>الرقم الضريبي:</strong> {providerVatNumber}</p>
                  {/* <p className="mb-1"><strong>الرقم الموحد:</strong> {providerUnifiedNo}</p> */}
                  <p className="mb-0"><strong>العنوان:</strong> {providerAddress}</p>
                </div>
              </Col>
            </Row>
            {settlement.period_start && settlement.period_end && (
              <p className="mb-0 text-muted text-center mt-3">
                <small>الفترة: من {formatDate(settlement.period_start)} إلى {formatDate(settlement.period_end)}</small>
              </p>
            )}
          </div>

          {/* Invoice Period Section */}
          <Row className="mb-4 pdf-section">
            <Col md={6}>
              <div className="mb-3 p-3 border rounded bg-info bg-opacity-10">
                <h6 className="mb-2">فترة الخدمة</h6>
                <p className="mb-1">
                  <strong>من تاريخ:</strong> {settlement.period_start ? formatDate(settlement.period_start) : "غير متوفر"}
                </p>
                <p className="mb-0">
                  <strong>إلى تاريخ:</strong> {settlement.period_end ? formatDate(settlement.period_end) : "غير متوفر"}
                </p>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <div className="mb-3 p-3 border rounded bg-warning bg-opacity-10">
                <h6 className="mb-2">تفاصيل التسوية</h6>
                <p className="mb-1">
                  <strong>عدد الفواتير:</strong> {settlement.invoice_count || 1}
                </p>
                <p className="mb-0">
                  <strong>حالة التسوية:</strong> {settlement.is_batched ? "مجمعة" : "فردية"}
                </p>
              </div>
            </Col>
          </Row>

          {/* Header Information */}
          <Row className="mb-4 pdf-section">
            <Col md={6}>
              <div className="mb-3">
                <h5>معلومات مقدم الخدمة</h5>
                <p className="mb-1">
                  <strong>الاسم:</strong> {providerName}
                </p>
                <p className="mb-1">
                  <strong>السجل التجاري:</strong> {providerCommercialRegister}
                </p>
                <p className="mb-1">
                  <strong>رقم الضريبة:</strong> {providerVatNumber}
                </p>
                <p className="mb-1">
                  {/* <strong>الرقم الموحد:</strong> {providerUnifiedNo} */}
                </p>
                <p className="mb-0">
                  <strong>العنوان:</strong> {providerAddress}
                </p>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <div className="mb-3">
                <h5>معلومات بيان التسوية</h5>
                <p className="mb-1">
                  <strong>رقم البيان:</strong> {settlement.id?.slice(0, 12) || "N/A"}
                </p>
                <p className="mb-1">
                  <strong>تاريخ الإنشاء:</strong> {formatDate(settlement.created_at)}
                </p>
                <p className="mb-1">
                  <strong>الحالة:</strong>
                  <Badge color={isRefundSettlement ? "danger" : "success"} className="ms-2">
                    {isRefundSettlement
                      ? "استرجاع"
                      : settlement.status === "generated"
                        ? "تم الإنشاء"
                        : settlement.status}
                  </Badge>
                </p>
                <p className="mb-1">
                  <strong>نوع العملية:</strong>
                  <Badge color={isRefundSettlement ? "danger" : "success"} className="ms-2">
                    {isRefundSettlement ? "تسوية استرجاع" : "تسوية عادية"}
                  </Badge>
                </p>
                {settlement?.batch_settlement_id && (
                  <p className="mb-1">
                    <strong>تسوية مجمعة:</strong>
                    <Badge color="info" className="ms-2">
                      نعم
                    </Badge>
                    <span className="text-muted ms-2">
                      ({settlement.invoice_count || 1} فواتير)
                    </span>
                  </p>
                )}
              </div>
            </Col>
          </Row>

          {/* Financial Details */}
          <div className="pdf-section settlement-details mb-4">
            <h5 className="mb-3">الملخص المالي - Financial Summary</h5>
            <Table bordered className="mb-0">
              <tbody>
                <tr>
                  <td><strong>المبلغ الأصلي للخدمة</strong><br/><small className="text-muted">Original Service Amount</small></td>
                  <td className="text-end">
                    <strong>{formatCurrency(settlement.amounts?.original_service_amount || settlement.amounts?.gross_service_amount)}</strong>
                  </td>
                </tr>
                {Number(settlement.amounts?.platform_commission || 0) > 0 && (
                  <tr className="table-warning">
                    <td><strong>عمولة المنصة (مخصومة)</strong><br/><small className="text-muted">Platform Commission (Deducted)</small></td>
                    <td className="text-end text-danger">
                      <strong>- {formatCurrency(settlement.amounts.platform_commission)}</strong>
                    </td>
                  </tr>
                )}
                {Number(settlement.amounts?.tamara_commission || 0) > 0 && (
                  <tr className="table-warning">
                    <td><strong>عمولة تمارا</strong></td>
                    <td className="text-end text-danger">- {formatCurrency(settlement.amounts.tamara_commission)}</td>
                  </tr>
                )}
                <tr className="table-info">
                  <td><strong>المبلغ بعد العمولة (قبل ض.ق.م)</strong><br/><small className="text-muted">Amount After Commission (Before VAT)</small></td>
                  <td className="text-end">
                    <strong>{formatCurrency(settlement.amounts?.amount_after_commission || settlement.amounts?.service_net)}</strong>
                  </td>
                </tr>
                <tr>
                  <td><strong>ضريبة القيمة المضافة (15%)</strong><br/><small className="text-muted">VAT (15%)</small></td>
                  <td className="text-end">
                    <strong>+ {formatCurrency(settlement.amounts?.service_vat)}</strong>
                  </td>
                </tr>
                <tr className="table-active">
                  <td><strong>الصافي مع الضريبة</strong><br/><small className="text-muted">Net Amount with VAT</small></td>
                  <td className="text-end">
                    <strong>{formatCurrency(
                      settlement.amounts?.net_amount_with_vat || 
                      (settlement.amounts?.amount_after_commission || settlement.amounts?.service_net || 0) * 1.15
                    )}</strong>
                  </td>
                </tr>
                {Number(settlement.amounts?.cancellation_fines || 0) > 0 && (
                  <tr className="table-warning">
                    <td><strong>غرامات الإلغاء (مخصومة)</strong><br/><small className="text-muted">Cancellation Fines (Deducted)</small></td>
                    <td className="text-end text-danger">
                      <strong>- {formatCurrency(settlement.amounts.cancellation_fines)}</strong>
                    </td>
                  </tr>
                )}
                <tr className="table-success">
                  <td>
                    <strong>{isRefundSettlement ? "المبلغ النهائي (استرجاع)" : "المبلغ النهائي المستحق"}</strong>
                    <br/>
                    <small className="text-muted">
                      {isRefundSettlement ? "Refund Settlement Amount" : "Final Payout Amount"}
                    </small>
                  </td>
                  <td className="text-end">
                    <strong style={{ fontSize: '1.1em', color: isRefundSettlement ? '#dc3545' : '#198754' }}>
                      {formatCurrency(
                        settlement.amounts?.final_payout ?? 
                        ((settlement.amounts?.net_amount_with_vat || settlement.amounts?.net_after_fines || 0) - 
                        (settlement.amounts?.cancellation_fines || 0))
                      )}
                    </strong>
                    {isRefundSettlement && (
                      <div><small className="text-muted">غير محتسب ضمن الإجماليات المالية</small></div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Deductions Details */}
          {Array.isArray(settlement.deductions) && settlement.deductions.length > 0 && (
            <div className="pdf-section settlement-details mb-4">
              <h5 className="mb-3">الخصومات المطبقة</h5>
              <Table bordered className="mb-0">
                <thead>
                  <tr>
                    <th>النوع</th>
                    <th>السبب</th>
                    <th className="text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {settlement.deductions.map((ded, idx) => (
                    <tr key={idx}>
                      <td>{ded.type === "cancellation_fine" ? "غرامة إلغاء" : ded.type || "خصم"}</td>
                      <td>{ded.reason || settlement.fine_reason || "غير محدد"}</td>
                      <td className="text-end text-danger">{formatCurrency(ded.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Transaction References */}
          <div className="pdf-section settlement-details mb-4">
            <h5 className="mb-3">المراجع والمعلومات الإضافية</h5>
            <Row>
              <Col md={6}>
                <p>
                  <strong>معرف الحجز:</strong> {settlement.booking_id || "غير متوفر"}
                </p>
                <p>
                  <strong>معرف الفاتورة:</strong> {settlement.invoice_id || "غير متوفر"}
                </p>
              </Col>
              <Col md={6} className="text-end">
                <p>
                  <strong>معرف فاتورة الخدمة:</strong> {settlement.erp_invoice_id || "غير متوفر"}
                </p>
                <p>
                  <strong>تاريخ إنشاء البيان:</strong> {formatDate(settlement.created_at)}
                </p>
              </Col>
            </Row>
          </div>

          {/* Footer */}
          <div className="pdf-section settlement-footer mt-4 pt-4 border-top">
            <p className="text-muted text-center">شكراً لاستخدامك خدماتنا</p>
            <p className="text-center text-muted" style={{ fontSize: "12px" }}>
              تم إنشاء هذا البيان بواسطة نظام إدارة DB Saloni
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="border-0">
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
        <Button color="success" onClick={downloadPDF} disabled={loadingPDF}>
          <FiDownload className="me-1" />
          {loadingPDF ? "جاري إنشاء PDF..." : "تحميل PDF"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ERP Invoices Tab Component
const ErpInvoicesTab = () => {
  const dispatch = useDispatch();
  const { invoices, loading, error } = useSelector((state) => state.providerErpInvoices);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const viewDetails = (invoice) => {
    setSelectedInvoice(invoice);
    toggleModal();
  };

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      return 'تاريخ غير صالح';
    }
  };

  const formatCurrency = (value) => {
    return `${(parseFloat(value) || 0).toFixed(2)} ر.س`;
  };

  const PaymentStatusBadge = ({ status }) => {
    const statusMap = {
      completed: { color: 'success', text: 'مكتمل' },
      pending: { color: 'warning', text: 'قيد الانتظار' },
      failed: { color: 'danger', text: 'فشل' },
      refunded: { color: 'info', text: 'تم الاسترجاع' },
      paid: { color: 'success', text: 'مدفوعة' }
    };

    const currentStatus = statusMap[status] || { color: 'secondary', text: status };
    
    return (
      <Badge color={currentStatus.color} className="p-2">
        {currentStatus.text}
      </Badge>
    );
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    const list = Array.isArray(invoices) ? invoices : [];
    
    const stats = {
      totalInvoices: list.length,
      totalAmount: 0,
      totalCommissions: 0,
      totalVAT: 0,
    };

    list.forEach((invoice) => {
      stats.totalAmount += Number(invoice?.total_platform_revenue || 0);
      stats.totalCommissions += Number(invoice?.total_commission || 0);
      stats.totalVAT += Number(invoice?.total_commission_vat || 0);
    });

    return stats;
  }, [invoices]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        width: 50
      },
      {
        Header: "رقم الفاتورة",
        accessor: "erp_invoice_id",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "اسم الخدمة",
        accessor: "service_name",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "إجمالي العمولة",
        accessor: "total_commission",
        Cell: ({ value }) => formatCurrency(value),
      },
      {
        Header: "ضريبة ق.م",
        accessor: "total_commission_vat",
        Cell: ({ value }) => formatCurrency(value),
      },
      {
        Header: "الإجمالي",
        accessor: "total_platform_revenue",
        Cell: ({ value }) => formatCurrency(value),
      },
      {
        Header: "حالة الدفع",
        accessor: "status",
        Cell: ({ value }) => <PaymentStatusBadge status={value} />,
      },
      {
        Header: "التاريخ",
        accessor: "transaction_date",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "التفاصيل",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button 
            color="primary" 
            size="sm" 
            onClick={() => viewDetails(row.original)}
            className="d-flex align-items-center"
          >
            <FiExternalLink className="me-1" />
            عرض
          </Button>
        ),
        width: 100
      },
    ],
    []
  );

  return (
    <>
      {/* Statistics Cards */}
      {!loading && !error && invoices.length > 0 && (
        <Row className="mb-4">
          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-primary shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">عدد الفواتير</h6>
                    <h4 className="text-primary">{statistics.totalInvoices}</h4>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#007bff", opacity: 0.3 }}>
                    📄
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-success shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">إجمالي العمولات</h6>
                    <h4 className="text-success">{formatCurrency(statistics.totalCommissions)}</h4>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#28a745", opacity: 0.3 }}>
                    💰
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-warning shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">ضريبة ق.م</h6>
                    <h4 className="text-warning">{formatCurrency(statistics.totalVAT)}</h4>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#ffc107", opacity: 0.3 }}>
                    📊
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={3} md={6} sm={12} className="mb-3">
            <Card className="border-top border-top-info shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-2">الإجمالي المستحق</h6>
                    <h4 className="text-info">{formatCurrency(statistics.totalAmount)}</h4>
                  </div>
                  <div style={{ fontSize: "2rem", color: "#17a2b8", opacity: 0.3 }}>
                    🏦
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">جاري تحميل البيانات...</p>
        </div>
      ) : error ? (
        <Alert color="danger">
          <h5 className="alert-heading">حدث خطأ!</h5>
          <p>{error}</p>
          <Button color="danger" size="sm" onClick={() => dispatch(fetchProviderErpInvoices())}>
            إعادة المحاولة
          </Button>
        </Alert>
      ) : invoices.length === 0 ? (
        <Alert color="info" className="text-center">
          لا توجد فواتير عمولة متاحة حالياً
        </Alert>
      ) : (
        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={invoices || []}
              isGlobalFilter={true}
              isAddOptions={false}
              customPageSize={10}
              className="custom-header-css"
            />
          </CardBody>
        </Card>
      )}

      <ErpInvoiceDetailsModal 
        isOpen={isModalOpen} 
        toggle={toggleModal} 
        invoice={selectedInvoice} 
      />
    </>
  );
};

// ERP Invoice Details Modal Component
const ErpInvoiceDetailsModal = ({ isOpen, toggle, invoice }) => {
  const [erpInvoice, setErpInvoice] = useState(null);
  const [loadingERP, setLoadingERP] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const invoiceRef = useRef();
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  const toNumber = (value) => Number(value || 0);
  const erpGrandTotal = toNumber(erpInvoice?.[0]?.grand_total ?? erpInvoice?.grand_total ?? invoice?.total_platform_revenue ?? 0);
  const totalCommissionVat = toNumber(invoice?.total_commission_vat);
  const totalRevenueExVat = Math.max(erpGrandTotal - totalCommissionVat, 0);
  const totalAppCommission = toNumber(invoice?.app_commission);
  const totalServiceProviderCommission = toNumber(invoice?.service_provider_commission);
  const totalTamaraCommission = toNumber(invoice?.tamara_commission);
  const totalTamaraCommissionVat = toNumber(invoice?.tamara_commission_vat);
  const totalCommission = toNumber(invoice?.total_commission);
  const totalPlatformRevenue = toNumber(invoice?.total_platform_revenue ?? erpGrandTotal);

  const fetchERPInvoice = async (invoiceId) => {
    try {
      setLoadingERP(true);
      const response = await fetch(
        `https://node-server-eta-coral.vercel.app/invoice-details/${encodeURIComponent(invoiceId)}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_NODE_API_KEY
          }
        }
      );
      const json = await response.json();
      setErpInvoice(json.data);
    } catch (err) {
      console.error("Error fetching ERP invoice:", err);
    } finally {
      setLoadingERP(false);
    }
  };

  const fetchPrivateImage = async (filePath) => {
    const response = await fetch(
      `https://node-server-eta-coral.vercel.app/erp-image?path=${encodeURIComponent(filePath)}`
    );
    if (!response.ok) throw new Error("Failed to fetch image");
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  useEffect(() => {
    if (!isOpen) return;
    const targetId = invoice?.erp_invoice_id || null;
    fetchERPInvoice(targetId);
  }, [isOpen, invoice?.erp_invoice_id]);

  useEffect(() => {
    if (!erpInvoice?.[0]?.ksa_einv_qr) return;

    setLoadingQR(true);
    fetchPrivateImage(erpInvoice[0].ksa_einv_qr)
      .then(setQrImageUrl)
      .catch(console.error)
      .finally(() => setLoadingQR(false));

    return () => {
      if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
    };
  }, [erpInvoice?.[0]?.ksa_einv_qr]);

  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          direction: rtl;
        }
        .invoice-header {
          border-bottom: 2px solid #000;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .invoice-title {
          color: #333;
        }
        .invoice-details {
          background-color: #f9f9f9;
          padding: 15px;
          border-radius: 5px;
        }
        .invoice-container .pdf-section,
        .invoice-container .invoice-details,
        .invoice-container .invoice-summary,
        .invoice-container .invoice-footer,
        .invoice-container .from-to-header,
        .invoice-container table,
        .invoice-container tr,
        .invoice-container .row,
        .invoice-container .qr-code-container {
          break-inside: avoid;
          page-break-inside: avoid;
        }
      }
    `,
  });

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      return 'تاريخ غير صالح';
    }
  };

  const downloadPDF = async () => {
    if (!invoice || !invoiceRef.current || loadingPDF) return;

    try {
      setLoadingPDF(true);
      await exportElementToPdf(invoiceRef.current, `commission_invoice_${invoice.id}.pdf`);
    } catch (error) {
      console.error("Error downloading commission PDF:", error);
    } finally {
      setLoadingPDF(false);
    }
  };

  if (!invoice) return null;

  const getDisplayValue = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      if (typeof value === "string" && value.trim() === "") continue;
      return value;
    }
    return "غير متوفر";
  };

  const platformInfo = invoice?.platform || {};
  const providerInfo =
    invoice?.service_provider ||
    invoice?.provider ||
    invoice?.current_provider ||
    {};

  const platformName = getDisplayValue(
    platformInfo.company_name,
    platformInfo.erp_admin_customer_name,
    "DB Saloni"
  );

  const providerName = getDisplayValue(
    providerInfo.name,
    providerInfo.company_name,
    invoice?.service_provider_name,
    invoice?.provider_name,
    invoice?.service_provider_id
  );

  const providerCommercialRegister = getDisplayValue(
    providerInfo.commercial_register,
    providerInfo.commercialRegister,
    providerInfo.cr_number,
    providerInfo.crNumber,
    invoice?.service_provider_commercial_register,
    invoice?.provider_commercial_register,
    invoice?.commercial_register
  );

  const providerVatNumber = getDisplayValue(
    providerInfo.vat_number,
    providerInfo.vatNumber,
    providerInfo.tax_number,
    providerInfo.taxNumber,
    providerInfo.tax_id,
    providerInfo.taxId,
    invoice?.service_provider_vat_number,
    invoice?.service_provider_tax_number,
    invoice?.provider_vat_number,
    invoice?.provider_tax_number,
    invoice?.tax_number,
    invoice?.vat_number
  );

  const providerUnifiedNo = getDisplayValue(
    providerInfo.entity_unified_no,
    providerInfo.entityUnifiedNo,
    providerInfo.unified_number,
    providerInfo.unifiedNumber,
    invoice?.service_provider_entity_unified_no,
    invoice?.provider_entity_unified_no,
    invoice?.entity_unified_no
  );

  const providerAddress = getDisplayValue(
    providerInfo.address,
    providerInfo.location,
    invoice?.service_provider_address,
    invoice?.provider_address,
    invoice?.address
  );

  const SkeletonBlock = ({ height = 16, width = "100%" }) => (
    <div
      style={{
        background: "#f0f0f0",
        height,
        width,
        borderRadius: 6,
        marginBottom: 8,
        animation: "pulse 1.2s ease-in-out infinite",
      }}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-0">
        <span className="modal-title">فاتورة العمولة - Commission Invoice</span>
      </ModalHeader>
      <ModalBody>
        {loadingERP ? (
          <div className="p-3">
            <div className="d-flex justify-content-between mb-3">
              <SkeletonBlock width="40%" height={18} />
              <SkeletonBlock width="30%" height={18} />
            </div>
            <SkeletonBlock height={20} width="60%" />
            <SkeletonBlock height={20} width="50%" />
            <SkeletonBlock height={20} width="70%" />
            <div className="d-flex gap-3 mt-3">
              <SkeletonBlock height={100} width="50%" />
              <SkeletonBlock height={100} width="50%" />
            </div>
            <div className="mt-3">
              <SkeletonBlock height={18} width="30%" />
              {[...Array(3)].map((_, i) => (
                <SkeletonBlock key={i} height={14} width={`${70 - i * 10}%`} />
              ))}
            </div>
            <style>{`
              @keyframes pulse {
                0% { opacity: 1; }
                50% { opacity: 0.4; }
                100% { opacity: 1; }
              }
            `}</style>
          </div>
        ) : (
          <div className="invoice-container" ref={invoiceRef}>
            <div className="invoice-header text-center mb-4 pdf-section">
              <h2 className="invoice-title">فاتورة العمولة</h2>
              <p className="text-muted">Commission Invoice - فاتورة إلكترونية</p>
            </div>

            <img 
              src="/logo.png"
              alt="Company Logo" 
              style={{ height: 60, objectFit: "contain" }} 
              className="mb-4 d-block mx-auto"
            />

            {/* From To Header */}
            <div className="from-to-header mb-4 p-3 pdf-section" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <Row className="g-3 align-items-stretch">
                <Col md={6}>
                  <div className="h-100 p-3 bg-white border rounded text-end">
                    <h6 className="mb-2">من: بيانات المنصة (DB Saloni)</h6>
                    <p className="mb-1"><strong>الاسم:</strong> {platformName}</p>
                    <p className="mb-1"><strong>السجل التجاري:</strong> {getDisplayValue(platformInfo.commercial_register)}</p>
                    <p className="mb-1"><strong>الرقم الضريبي:</strong> {getDisplayValue(platformInfo.vat_number)}</p>
                    {/* <p className="mb-1"><strong>الرقم الموحد:</strong> {getDisplayValue(platformInfo.entity_unified_no)}</p> */}
                    <p className="mb-0"><strong>العنوان:</strong> {getDisplayValue(platformInfo.address)}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="h-100 p-3 bg-white border rounded">
                    <h6 className="mb-2">إلى: بيانات مقدم الخدمة</h6>
                    <p className="mb-1"><strong>الاسم:</strong> {providerName}</p>
                    <p className="mb-1"><strong>السجل التجاري:</strong> {providerCommercialRegister}</p>
                    <p className="mb-1"><strong>الرقم الضريبي:</strong> {providerVatNumber}</p>
                    {/* <p className="mb-1"><strong>الرقم الموحد:</strong> {providerUnifiedNo}</p> */}
                    <p className="mb-0"><strong>العنوان:</strong> {providerAddress}</p>
                  </div>
                </Col>
              </Row>
            </div>
            
            <Row className="mb-4 pdf-section">
              <Col md={6}>
                <div className="provider-info mb-3" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px' }}>
                  <h5 className="mb-3">بيانات الخدمة</h5>
                  <p className="mb-1"><strong>اسم الخدمة:</strong> {invoice.service_name || 'غير متوفر'}</p>
                  <p className="mb-1"><strong>اسم العميل:</strong> {invoice.customer_name || 'غير متوفر'}</p>
                  <p className="mb-0"><strong>تاريخ الخدمة:</strong> {formatDate(invoice.transaction_date)}</p>
                </div>
              </Col>
              <Col md={6} className="text-end">
                <div className="invoice-info mb-3" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px' }}>
                  <h5 className="mb-3">معلومات الفاتورة</h5>
                  <p className="mb-1"><strong>رقم الفاتورة:</strong> {erpInvoice?.[0]?.name || "N/A"}</p>
                  <p className="mb-1"><strong>تاريخ الفاتورة:</strong> {formatDate(erpInvoice?.[0]?.posting_date)}</p>
                  <p className="mb-0"><strong>حالة الفاتورة:</strong> 
                    <Badge color={invoice.status === 'paid' ? 'success' : invoice.status === 'pending' ? 'warning' : 'secondary'} className="ms-2">
                      {invoice.status === 'paid' ? 'مدفوعة' : invoice.status === 'pending' ? 'قيد الانتظار' : invoice.status}
                    </Badge>
                  </p>
                </div>
              </Col>
            </Row>
            
            <div className="invoice-details mb-4 pdf-section">
              <h5 className="mb-3">تفاصيل المعاملة</h5>
              <Row>
                <Col md={6}>
                  <p><strong>الخدمة:</strong> {invoice.service_name || 'غير متوفر'}</p>
                  <p><strong>المبلغ الأصلي:</strong> {toNumber(invoice?.total_after_discount || invoice?.gross_service_amount).toFixed(2)} ر.س</p>
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                  <div className="qr-code-container" style={{ 
                    backgroundColor: 'white', 
                    padding: '10px',
                    border: '1px solid #eee',
                    borderRadius: '4px'
                  }}>
                    {loadingQR ? (
                      <div className="d-flex align-items-center justify-content-center" style={{ width: 128, height: 128 }}>
                        <Spinner size="sm" color="primary" />
                      </div>
                    ) : qrImageUrl ? (
                      <img
                        src={qrImageUrl}
                        alt="ZATCA QR Code"
                        style={{ width: 128, height: 128, objectFit: "contain" }}
                      />
                    ) : (
                      <div style={{ width: 128, height: 128, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                        لا يوجد QR Code
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
            
            <div className="invoice-summary pdf-section">
              <h5 className="mb-3">ملخص فاتورة العمولة</h5>
              <Table bordered>
                <thead>
                  <tr>
                    <th>DB Saloni</th>
                    <th className="text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>عمولة التطبيق</td>
                    <td className="text-end">{totalAppCommission.toFixed(2)} ر.س</td>
                  </tr>
                  <tr>
                    <td>عمولة مقدم الخدمة</td>
                    <td className="text-end">{totalServiceProviderCommission.toFixed(2)} ر.س</td>
                  </tr>
                  <tr>
                    <td>عمولة تمارا</td>
                    <td className="text-end">{totalTamaraCommission.toFixed(2)} ر.س</td>
                  </tr>
                  <tr>
                    <td>إجمالي العمولات (قبل الضريبة)</td>
                    <td className="text-end">{totalCommission.toFixed(2)} ر.س</td>
                  </tr>
                  <tr>
                    <td>ضريبة القيمة المضافة (15%)</td>
                    <td className="text-end">{totalCommissionVat.toFixed(2)} ر.س</td>
                  </tr>
                  {totalTamaraCommissionVat > 0 && (
                    <tr>
                      <td>ضريبة عمولة تمارا</td>
                      <td className="text-end">{totalTamaraCommissionVat.toFixed(2)} ر.س</td>
                    </tr>
                  )}
                  {toNumber(invoice?.fine_deductions_total) > 0 && (
                    <tr className="table-warning">
                      <td>
                        <strong>غرامات الإلغاء</strong>
                        {invoice.fine_reason && (
                          <div className="text-muted" style={{ fontSize: '0.85em' }}>
                            {invoice.fine_reason}
                          </div>
                        )}
                      </td>
                      <td className="text-end text-danger"><strong>{toNumber(invoice?.fine_deductions_total).toFixed(2)} ر.س</strong></td>
                    </tr>
                  )}
                  <tr className="table-active">
                    <td><strong>الإجمالي</strong></td>
                    <td className="text-end"><strong>{totalPlatformRevenue.toFixed(2)} ر.س</strong></td>
                  </tr>
                </tbody>
              </Table>
            </div>
            
            {/* ERP details section */}
            {Array.isArray(erpInvoice?.items) && erpInvoice.items.length > 0 && (
              <div className="invoice-details mb-4 pdf-section">
                <h6 className="mb-2">المنتجات/الخدمات</h6>
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>الكمية</th>
                      <th className="text-end">السعر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {erpInvoice.items.map((it) => (
                      <tr key={it.name}>
                        <td>{it.item_name || it.description || '—'}</td>
                        <td>{it.qty ?? 0}</td>
                        <td className="text-end">{it.rate ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            
            <div className="invoice-footer mt-4 pt-4 border-top pdf-section">
              <p className="text-muted text-center">شكراً لاستخدامك خدماتنا</p>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="border-0">
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
        <Button color="success" onClick={downloadPDF} disabled={loadingPDF}>
          <FiDownload className="me-1" />
          {loadingPDF ? "جاري إنشاء PDF..." : "تحميل PDF"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SettlementStatements;



963 
